import React, { useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { withTheme } from 'styled-components'
import { get } from 'lodash'
import { connect } from 'react-redux'

import {
  FlexContainer,
  QuestionNumberLabel,
  AnswerContext,
  QuestionSubLabel,
  QuestionLabelWrapper,
  QuestionContentWrapper,
  TextAndMathEditor,
} from '@edulastic/common'
import { withNamespaces } from '@edulastic/localization'
import { lightGrey12 } from '@edulastic/colors'
import { sanitizeHtml } from '@edulastic/common/src/utils/html'
import { PREVIEW } from '../../constants/constantsForQuestions'

import { InlineMathEssayContainer, QuestionTitleWrapper } from './styled'
import { StyledPaperWrapper } from '../../styled/Widget'
import Instructions from '../../components/Instructions'
import StackedTranslatorStimulus from '../../components/Common/StackedTranslator/StackedTranslatorStimulus'
import { getTestDataSelector } from '../../../author/ClassBoard/ducks'

const InlineMathEssayPreview = ({
  col,
  view,
  saveAnswer,
  item,
  smallSize,
  userAnswer,
  showQuestionNumber,
  disableResponse,
  previewTab,
  isPrintPreview,
  isStudentAttempt,
  isFeedbackVisible,
  subjects,
  grades,
  viewAsStudent,
  isTestDemoPlayer,
  isTestPreviewModalVisible,
  isExpressGrader,
  expressGraderTestData,
}) => {
  userAnswer = typeof userAnswer === 'object' ? '' : userAnswer
  const answerContextConfig = useContext(AnswerContext)

  let minHeight = get(item, 'uiStyle.minHeight', 200)
  const maxHeight = get(item, 'uiStyle.maxHeight', 300)
  minHeight -= 1
  // minHeight -1 cuz when maxHeight and minHeight is same,
  // div with maxHeight has border 1px which is parent of div with minHeight,
  // hence parent div  height is shrinked by 1px, hence scroll bar appears
  // border 1px is coming from froala editor default css which is necessary

  useEffect(() => {
    if (Array.isArray(userAnswer) || typeof userAnswer !== 'string') {
      saveAnswer('')
    }
  }, [userAnswer])

  const handleTextChange = (val) => {
    if (typeof val === 'string') {
      saveAnswer(val)
    }
  }

  const isV1Multipart = get(col, 'isV1Multipart', false)

  /**
   * if answerContextConfig comes from LCB/EG pages
   */
  const isReadOnly =
    (previewTab === 'show' && !answerContextConfig.isAnswerModifiable) ||
    disableResponse

  const isTestPreview =
    viewAsStudent || isTestDemoPlayer || isTestPreviewModalVisible

  subjects = isExpressGrader ? expressGraderTestData?.subjects || [] : subjects
  grades = isExpressGrader ? expressGraderTestData?.grades || [] : grades

  return item.id ? (
    <StyledPaperWrapper
      isV1Multipart={isV1Multipart}
      padding={smallSize}
      boxShadow={smallSize ? 'none' : ''}
    >
      <FlexContainer justifyContent="flex-start" alignItems="baseline">
        <QuestionLabelWrapper>
          {showQuestionNumber && (
            <QuestionNumberLabel>{item.qLabel}</QuestionNumberLabel>
          )}
          {item.qSubLabel && (
            <QuestionSubLabel>({item.qSubLabel})</QuestionSubLabel>
          )}
        </QuestionLabelWrapper>

        <QuestionContentWrapper showQuestionNumber={showQuestionNumber}>
          <QuestionTitleWrapper data-cy="questionTitle">
            {view === PREVIEW && !smallSize && (
              <StackedTranslatorStimulus
                question={item}
                defaultStimulus={item.stimulus}
              />
            )}
          </QuestionTitleWrapper>
          <InlineMathEssayContainer
            className="ignore-for-ir-content"
            reduceWidth={
              isStudentAttempt && isFeedbackVisible ? '150px' : '0px'
            }
            data-cy="previewBoxContainer"
          >
            {!Array.isArray(userAnswer) && !isReadOnly && !isPrintPreview && (
              <TextAndMathEditor
                onChange={handleTextChange}
                value={userAnswer}
                readOnly={
                  !answerContextConfig.isAnswerModifiable || disableResponse
                }
                placeholder={item?.placeholder}
                editorHeight={minHeight}
                className={`essay-rich-text-${item.id}`}
                subjects={subjects}
                grades={grades}
                isStudentAttempt={isStudentAttempt}
                isTestPreview={isTestPreview}
                isExpressGrader={isExpressGrader}
                maxHeight={maxHeight}
              />
            )}
            {((!Array.isArray(userAnswer) && isReadOnly) ||
              (!Array.isArray(userAnswer) && isPrintPreview)) && (
              <FlexContainer
                alignItems="flex-start"
                justifyContent="flex-start"
                style={{
                  minHeight: '150px',
                  borderRadius: '4px 4px 0 0',
                  border: `1px solid ${lightGrey12}`,
                  padding: '6px',
                  margin: '-1px', // Parent component has borders
                }}
              >
                <TextAndMathEditor
                  value={sanitizeHtml(userAnswer || '')}
                  readOnly
                  editorHeight={minHeight}
                  className={`essay-rich-text-${item.id}`}
                  maxHeight={maxHeight}
                />
              </FlexContainer>
            )}
          </InlineMathEssayContainer>
        </QuestionContentWrapper>
      </FlexContainer>
      <Instructions item={item} />
    </StyledPaperWrapper>
  ) : null
}

InlineMathEssayPreview.propTypes = {
  smallSize: PropTypes.bool,
  item: PropTypes.object.isRequired,
  saveAnswer: PropTypes.func.isRequired,
  view: PropTypes.string.isRequired,
  userAnswer: PropTypes.any,
  previewTab: PropTypes.string.isRequired,
  showQuestionNumber: PropTypes.bool,
  isPrintPreview: PropTypes.bool.isRequired,
  disableResponse: PropTypes.bool.isRequired,
  col: PropTypes.object,
  isFeedbackVisible: PropTypes.bool,
  isStudentAttempt: PropTypes.bool,
}

InlineMathEssayPreview.defaultProps = {
  smallSize: false,
  userAnswer: '',
  showQuestionNumber: false,
  col: {},
  isFeedbackVisible: false,
  isStudentAttempt: false,
}

const enhance = compose(
  withNamespaces('assessment'),
  withTheme,
  connect((state) => ({
    subjects: state?.test?.subjects || [],
    grades: state?.test?.grades || [],
    expressGraderTestData: getTestDataSelector(state),
  }))
)

export default enhance(InlineMathEssayPreview)
